import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isSignInWithEmailLink, signInWithEmailLink } from "firebase/auth";
import { auth } from "../../../config/firebase";
import { Notice } from "../../common/cards/Notice";
import { useLogger } from "../../../context/LoggerContext";
import { Login } from "./Login";
import { validateEmail } from "../../../helpers/ValidationHelpers";
import { Loader } from "../../common/modals/LoaderModal";

interface VerifyEmailProps {
  ExistsRedirectPath?: string;
}

export function VerifyEmail({ ExistsRedirectPath = "/dashboard" }: VerifyEmailProps) {
  const { logAppInsightsError } = useLogger();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const handleAuthError = useCallback((err: unknown) => {
    const code = (err as { code?: string } | null)?.code;
    if (code === "auth/invalid-action-code") {
      window.localStorage.removeItem("emailForSignIn");

      setAuthError("This sign-in link has expired. Please use the below to receive a new sign-in link.");
      return;
    }

    if (code === "auth/invalid-email") {
      setFormError("Invalid email. Please check and try again.");
      return;
    }

    setAuthError("Failed to verify email. Please try again.");
    logAppInsightsError(err, "Verify Email");
  }, [logAppInsightsError]);

  // Every account this page handles - old-lander, plain magic-link, and the anonymous-upgrade
  // flow - resolves to a normal existing-user sign-in-link completion. The anonymous-upgrade
  // flow no longer needs a special case here: by the time its link is ever sent, the account
  // already exists and any prior quote data has already been carried over server-side.
  const completeSignIn = useCallback(async (email: string) => {
    await signInWithEmailLink(auth, email, window.location.href);

    window.localStorage.removeItem("emailForSignIn");

    const redirectUrl = searchParams.get("redirect") || ExistsRedirectPath;
    navigate(decodeURI(redirectUrl), { replace: true });
  }, [ExistsRedirectPath, navigate, searchParams]);

  useEffect(() => {
    const verifyEmail = async () => {
      setLoading(true);
      setAuthError(null);

      try {
        if (!isSignInWithEmailLink(auth, window.location.href)) {
          setAuthError("Invalid or expired sign-in link");
          return;
        }

        const storedEmail = window.localStorage.getItem("emailForSignIn");

        if (!storedEmail) {
          return;
        }

        await completeSignIn(storedEmail);
      } catch (err) {
        handleAuthError(err);
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [completeSignIn, handleAuthError]);

  const handleEmailConfirmationSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const normalizedEmail = confirmEmail.trim().toLowerCase();

    if (!normalizedEmail) {
      setFormError("Please enter your email address");
      return;
    }

    if (!validateEmail(normalizedEmail)) {
      setFormError("Please enter a valid email address.");
      return;
    }
    setFormError(null);
    setIsSubmitting(true);
    try {
      window.localStorage.setItem("emailForSignIn", normalizedEmail);
      await completeSignIn(normalizedEmail);
    } catch (err) {
      handleAuthError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trimStart();
    setConfirmEmail(value);
    if (formError && validateEmail(value.trim().toLowerCase())) {
      setFormError(null);
    }
  };

  const handleBlur = () => {
    const normalizedEmail = confirmEmail.trim().toLowerCase();
    if (!normalizedEmail) {
      setFormError("Please enter your email address");
      return;
    }

    if (!validateEmail(normalizedEmail)) {
      setFormError("Please enter a valid email address.");
      return;
    }

    setFormError(null);
  };

  if (loading) return <Loader />

  if (authError) {
    return (
      <Login
        notice={
          <Notice
            noticeClasses="notice d-flex bg-light-danger rounded border-danger border-dashed p-6 mb-7"
            button={null}
            header={"There was a problem..."}
            content={authError}
            icon={null}
            iconClasses={null}
          />
        }
      />
    )
  }

  return (
    <div className="card card-rounded w-100" style={{ maxWidth: "600px" }}>
      <div className="card-body d-flex flex-column gap-10">
        <p className="text-dark fs-1 fw-bolder mb-10 text-center">
          Confirm your email to sign in
        </p>

        <form onSubmit={handleEmailConfirmationSubmit}>
          <div className="d-flex flex-column gap-2 mb-10">
            <label className="form-label fw-bolder text-dark fs-6 m-0">Email</label>
            <input
              type="email"
              name="email"
              onChange={handleChange}
              onBlur={handleBlur}
              className={`form-control bg-transparent fs-4 ${formError ? "border-1 border-danger text-danger" : ""}`}
              autoComplete="email"
              value={confirmEmail}
              disabled={isSubmitting}
              required
            />
            {formError &&
              <span className="text-danger">{formError}</span>
            }
          </div>
          <button type="submit" className="btn btn-lg btn-primary w-100 position-relative" disabled={isSubmitting}>
            {!isSubmitting ?
              (
                <span>Confirm your email</span>
              ) : (
                <>
                  <span>Confirming...</span>
                  <div className="spinner-border position-absolute" role="status" style={{ height: "25px", width: "25px", right: "15px" }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </>
              )}
          </button>
        </form>
      </div>
    </div>
  );
}
